import { useCallback, useState } from "react";
import { updateUserSocials } from "../../services/userProfileService";
import { ExternalConnection } from "../../types/User";
import { debugLogError } from "../../utils/debug-log-error";

const SensitiveConnectionTypes = ["email", "phone"];

function mapConnectionTypeToPlaceholder(
  type: ExternalConnection["type"]
): string {
  switch (type) {
    case "instagram":
      return "Enter your Instagram handle...";
    case "linkedin":
      return "Enter your LinkedIn profile URL...";
    case "email":
      return "Enter your email address...";
    case "phone":
      return "Enter your phone number...";
    case "text":
    case "none":
      return "Enter a connection value...";
  }
  return "Enter a connection value...";
}

interface SocialsUpdateFormProps {
  userUid: string | null;
  socials: ExternalConnection[];
  onError: (error: Error) => void;
  onClearError: () => void;
}

const SocialsUpdateForm: React.FC<SocialsUpdateFormProps> = ({
  userUid,
  socials,
  onError,
  onClearError,
}) => {
  const [hasChanged, setHasChanged] = useState(false);
  const [connections, setConnections] = useState<ExternalConnection[]>(socials);

  const handleSaveSocials = useCallback(async () => {
    setHasChanged(false);

    if (!userUid || !hasChanged) {
      return;
    }

    const connectionsToSave = connections.filter(
      (c) => c.type !== "none" && c.value.trim().length > 1
    );

    onClearError();
    try {
      await updateUserSocials(userUid, connectionsToSave);
      setConnections(connectionsToSave);
    } catch (err) {
      debugLogError("ProfilePage::handleSaveSocials", err);
      onError(new Error("Failed to update socials. Please try again."));
    }
  }, [connections, userUid, hasChanged, onError, onClearError, setConnections]);

  const handleAddConnection = useCallback(() => {
    setHasChanged(true);
    setConnections([...connections, { type: "none", value: "" }]);
  }, [connections]);

  const handleConnectionChange = useCallback(
    (index: number, value: string) => {
      setHasChanged(true);
      setConnections(
        connections.map((connection, i) =>
          i === index ? { ...connection, value } : connection
        )
      );
    },
    [connections]
  );

  const handleConnectionTypeChange = useCallback(
    (index: number, type: string) => {
      setHasChanged(true);
      setConnections(
        connections.map((connection, i) =>
          i === index
            ? { ...connection, type: type as ExternalConnection["type"] }
            : connection
        )
      );
    },
    [connections]
  );

  const handleRemoveConnection = useCallback(
    (index: number) => {
      setHasChanged(true);
      setConnections(connections.filter((_, i) => i !== index));
    },
    [connections]
  );

  return (
    <div>
      <div className="form-group socials-section">
        <div className="form-label">
          <span>Social Media &amp; Messaging</span>
        </div>
        <div className="form-caption">
          Add links to your social media and preferred messaging apps here.
        </div>
        <div className="form-content">
          {connections.map((connection, index) => (
            <div key={`${connection.type}-${index}`} className="connection-row">
              <select
                className="form-select"
                value={connection.type}
                onChange={(e) =>
                  handleConnectionTypeChange(index, e.target.value)
                }
              >
                <option value="">Select a connection type...</option>
                <option value="email">Email Address</option>
                <option value="instagram">Instagram</option>
                <option value="linkedin">LinkedIn</option>
                <option value="text">Other</option>
                <option value="phone">Phone Number</option>
              </select>
              <div className="form-input">
                <input
                  type="text"
                  className="form-control"
                  placeholder={
                    connection.type === "none"
                      ? "Enter a connection value..."
                      : mapConnectionTypeToPlaceholder(connection.type)
                  }
                  onChange={(e) =>
                    handleConnectionChange(index, e.target.value)
                  }
                  value={connection.value}
                />
                <span
                  className="form-caption clickable"
                  onClick={() => handleRemoveConnection(index)}
                >
                  Remove
                </span>
                {SensitiveConnectionTypes.includes(connection.type) && (
                  <div className="form-caption">
                    As a reminder, this information will be visible to other
                    users.
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="form-actions">
          <button
            className="btn btn-outline-primary"
            type="button"
            onClick={handleAddConnection}
            aria-label="Add connection"
          >
            Add Connection
          </button>
          {hasChanged && (
            <button className="btn btn-primary" onClick={handleSaveSocials}>
              Save
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialsUpdateForm;
